/**
 * Client-side API input validation and sanitization (H-6).
 * Defense-in-depth only — backend must enforce authorization and business rules.
 */

export const MAX_STRING_LENGTH = 8192;
export const MAX_OBJECT_DEPTH = 10;

/**
 * Escapes HTML special characters to prevent XSS when inserting
 * untrusted data into HTML strings (e.g., D3 `.html()` tooltips).
 */
const HTML_ESCAPE_MAP = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
export function escapeHtml(str) {
  if (typeof str !== "string") return String(str ?? "");
  return str.replace(/[&<>"']/g, ch => HTML_ESCAPE_MAP[ch]);
}

function hasUnsafeUrlCharacters(url) {
  for (let i = 0; i < url.length; i += 1) {
    const code = url.charCodeAt(i);
    if (code < 32 || code === 127) {
      return true;
    }
  }
  return url.includes("..") || url.startsWith("//") || url.includes("://");
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SERIAL_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
const ALLOWED_LOGIN_LOCALES = new Set(["en", "es", "fr", "de", "nl", "it", "pt", "ja", "ko", "zh"]);

const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);

export class ApiValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = "ApiValidationError";
  }
}

export function assertSafeRelativeUrl(url) {
  if (typeof url !== "string" || !url.startsWith("/")) {
    throw new ApiValidationError("API URL must be a relative path starting with /");
  }
  if (hasUnsafeUrlCharacters(url)) {
    throw new ApiValidationError("API URL contains invalid characters or path traversal");
  }
}

export function sanitizeString(value) {
  const stripped = String(value).replace(/\0/g, "").trim();
  return stripped.length > MAX_STRING_LENGTH ? stripped.slice(0, MAX_STRING_LENGTH) : stripped;
}

function sanitizeValue(value, depth) {
  if (depth > MAX_OBJECT_DEPTH) {
    throw new ApiValidationError(`Request payload exceeds maximum depth of ${MAX_OBJECT_DEPTH}`);
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "string") {
    return sanitizeString(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => sanitizeValue(item, depth + 1));
  }

  if (typeof value === "object") {
    if (value instanceof Date) {
      return value.toISOString();
    }
    const result = {};
    Object.keys(value).forEach(key => {
      if (FORBIDDEN_KEYS.has(key)) {
        return;
      }
      result[key] = sanitizeValue(value[key], depth + 1);
    });
    return result;
  }

  return undefined;
}

/**
 * Deep-sanitizes JSON-serializable request params/bodies.
 * Skipped for FormData/Blob (pass through unchanged).
 */
export function sanitizeRequestData(data) {
  if (data === null || data === undefined) {
    return data;
  }
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    return data;
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return data;
  }
  if (typeof data !== "object") {
    return sanitizeValue(data, 0);
  }
  return sanitizeValue(data, 0);
}

export function isValidEmail(value) {
  if (typeof value !== "string") return false;
  const email = value.trim();
  if (!email || email.length > 254) return false;
  return EMAIL_PATTERN.test(email);
}

export function isValidSerialNumber(value) {
  if (typeof value !== "string") return false;
  const serial = value.trim();
  if (!serial) return false;
  return SERIAL_PATTERN.test(serial);
}

/**
 * Sanitizes and encodes a single URL path segment.
 */
export function sanitizePathParam(value, { pattern = SERIAL_PATTERN, label = "path parameter" } = {}) {
  if (value === null || value === undefined) {
    throw new ApiValidationError(`Invalid ${label}`);
  }
  const normalized = sanitizeString(String(value));
  if (!pattern.test(normalized)) {
    throw new ApiValidationError(`Invalid ${label}`);
  }
  return encodeURIComponent(normalized);
}

export function sanitizeEmailPathParam(email) {
  if (!isValidEmail(email)) {
    throw new ApiValidationError("Invalid email");
  }
  return encodeURIComponent(email.trim());
}

export function sanitizeSerialPathParam(serialNumber) {
  return sanitizePathParam(serialNumber, { pattern: SERIAL_PATTERN, label: "serial number" });
}

export function validateLoginPayload(credentials) {
  if (!credentials || typeof credentials !== "object") {
    throw new ApiValidationError("Invalid login credentials");
  }

  const email = typeof credentials.email === "string" ? credentials.email.trim() : "";
  const password = typeof credentials.password === "string" ? credentials.password : "";
  const locale =
    typeof credentials.locale === "string" && credentials.locale.trim()
      ? credentials.locale.trim().toLowerCase()
      : "en";

  if (!isValidEmail(email)) {
    throw new ApiValidationError("Invalid email address");
  }

  // SECURITY FIX M-7: Basic password validation for login
  // (Strength validation only needed for registration/password change)
  if (!password || password.length > 256) {
    throw new ApiValidationError("Invalid password");
  }

  if (!ALLOWED_LOGIN_LOCALES.has(locale)) {
    throw new ApiValidationError("Invalid locale");
  }

  return { email, password, locale };
}
